
import React from 'react';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar, DollarSign, User, Hash, CheckCircle, CreditCard, Clock, CreditCard as PaymentMethodIcon } from 'lucide-react';
import { Button } from "@/components/ui/button";

interface ReferrerPayment {
  id: string;
  referDate: string;
  referrerId: string;
  referrerName: string;
  accountNumber: string;
  approvedReferrals: number;
  totalPoints: number;
  amountDue: number;
  amountPaid: number | null;
  paidBy: string | null;
  datePaid: string | null;
  paidMethod?: string | null;
}

interface ReferrerPaymentReportProps {
  payments: ReferrerPayment[];
  onMarkAsPaid: (id: string, amount: number, paidBy: string) => void;
}

const ReferrerPaymentReport: React.FC<ReferrerPaymentReportProps> = ({ 
  payments,
  onMarkAsPaid 
}) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Referrer Payment Report</h2>
      
      <Table>
        <TableCaption>Monthly payments due to referrers</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>
              <div className="flex items-center">
                <Calendar className="mr-2 h-4 w-4" />
                Refer Date
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <Hash className="mr-2 h-4 w-4" />
                Referral Code
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <User className="mr-2 h-4 w-4" />
                Referrer Name
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <CreditCard className="mr-2 h-4 w-4" />
                Account Number
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <CheckCircle className="mr-2 h-4 w-4" />
                Approved Referrals
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <Hash className="mr-2 h-4 w-4" />
                Total Points
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <DollarSign className="mr-2 h-4 w-4" />
                Amount Due
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <DollarSign className="mr-2 h-4 w-4" />
                Amount Paid
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <User className="mr-2 h-4 w-4" />
                Paid By
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <Clock className="mr-2 h-4 w-4" />
                Date Paid
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center">
                <CreditCard className="mr-2 h-4 w-4" />
                Paid Method
              </div>
            </TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.length > 0 ? (
            payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{payment.referDate}</TableCell>
                <TableCell className="font-medium text-blue-600">{payment.referrerId}</TableCell>
                <TableCell>{payment.referrerName}</TableCell>
                <TableCell>{payment.accountNumber}</TableCell>
                <TableCell>{payment.approvedReferrals}</TableCell>
                <TableCell>{payment.totalPoints}</TableCell>
                <TableCell>${payment.amountDue.toFixed(2)}</TableCell>
                <TableCell>{payment.amountPaid ? `$${payment.amountPaid.toFixed(2)}` : '-'}</TableCell>
                <TableCell>{payment.paidBy || '-'}</TableCell>
                <TableCell>{payment.datePaid || '-'}</TableCell>
                <TableCell>
                  {payment.paidMethod ? (
                    <span className="text-blue-600">{payment.paidMethod}</span>
                  ) : '-'}
                </TableCell>
                <TableCell className="text-right">
                  {!payment.amountPaid && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onMarkAsPaid(payment.id, payment.amountDue, 'Admin')}
                    >
                      Mark as Paid
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={12} className="text-center py-4 text-gray-500">
                No payment records found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ReferrerPaymentReport;
